import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { AccessRequirement } from '@app/core/models/access-requirement.model';
import { EmailTemplateType } from '@app/core/models/emails/email-template-type.model';
import { InvoiceInjection } from '@app/core/models/invoices/injection/invoice-injection.interface';
import { InvoiceInjectionModel } from '@app/core/models/invoices/injection/invoice-injection.model';
import { InvoiceActionType } from '@app/core/models/invoices/invoice-action-type.model';
import { InvoiceAmendmentDetail } from '@app/core/models/invoices/invoice-amendment.model';
import { InvoiceComplaint } from '@app/core/models/invoices/invoice-complaint.model';
import { InvoiceDeductionDetail } from '@app/core/models/invoices/invoice-deduction.model';
import { InvoiceDelegationDetail } from '@app/core/models/invoices/invoice-delegation.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceExpenseDetail } from '@app/core/models/invoices/invoice-expense.model';
import { InvoiceFilesForCreate } from '@app/core/models/invoices/invoice-files-for-create';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { RejectionFlags } from '@app/core/models/referentials/rejection-flag.model';
import { WorkflowStageValidationModel } from '@app/core/models/workflows/workflow-stage-validation.model';
import { InvoiceService } from '@app/core/services/invoice.service';
import { InvoiceCancelComponent } from '@app/features/invoices/actions/cancel/invoice-cancel.component';
import { InvoiceForwardComponent } from '@app/features/invoices/actions/forward/invoice-forward.component';
import { WorkflowStageValidationComponent } from '@app/features/workflow-stage-validation/workflow-stage-validation.component';
import { AttachmentComponent } from '@app/shared/components/attachment/attachment.component';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { UploadComponent } from '@app/shared/components/upload/upload.component';
import { WriteCommentComponent } from '@app/shared/components/write-comment/write-comment.component';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { NumberCommaToDotFormatPipe } from '@app/shared/pipes/number-comma-to-dot-format.pipe';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { InvoiceListPPRErrorsComponent } from '../../actions/listPPRErrors/invoice-list-pprerrors/invoice-list-pprerrors.component';
import { InvoicePPRLogModel } from '@app/core/models/invoices/invoice-pprlog.model';

const qualificationPipelineStates = [
  'PendingForComplianceCheck',
  'PendingForDuplicateCheck',
  'PendingForQualificationCheck',
];

const processedState = ['Processed'];

@Component({
  selector: 'app-invoice-detail-actions',
  templateUrl: './invoice-detail-actions.component.html',
  styleUrls: ['./invoice-detail-actions.component.scss'],
})
export class InvoiceDetailActionsComponent implements OnInit, OnDestroy {
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  isDispatchedInvoice: boolean = false;
  isSearchDetail: boolean = false;
  isRejectedInvoice: boolean = false;
  isPendingForWorkflowCompletionVoucher: boolean = false;
  isPendingForWorkflowCompletionPromotion: boolean = false;
  isPendingForVoucherValidation: boolean = false;
  isNonCompliantInvoice: boolean = false;
  isDuplicateInvoice: boolean = false;
  isSupervisor: boolean = false;
  isPickedByMe: boolean = false;
  isPickedNotByMe: boolean = false;
  isComplaintByMe: boolean = false;
  isComplaintNotByMe: boolean = false;
  isControl: boolean = false;
  isInQualificationPipeline: boolean = false;
  isInProcessedStateState: boolean = false;
  showValidationButton: boolean = false;
  showCancelValidationPromotionButton: boolean = false;
  showComplaintPromotionButton: boolean = false;
  IsInjection: boolean = false;
  isManual: boolean = false;
  showGoToErrorButton: boolean = false;
  isWaitingVoucher: boolean = false;
  hasControlMembership: boolean = false;
  hasOperationalMembership: boolean = false;
  hideInvoicePPRLogErrorHistory: boolean = false;
  isInErrorPPRState: boolean = false;
  isComplaint: boolean = false;

  isUserInCancelationPromotionGroup: boolean = false;

  file: File;
  filename: string;

  hasAdministrationGrant: boolean;
  isToControlInWaiting: boolean = false;

  @Input() invoiceViewType: string;
  @Input() invoice: InvoiceDetailModel;
  @Input() injection: InvoiceInjection = null;
  @Input() canCancelProcess: boolean = false;
  @Input() canManageInvoiceAGI: boolean = false;
  @Input() invoiceForm: UntypedFormGroup = null;
  @Input() invoiceFormHasFiles: boolean = false;
  @Input() invoiceFormFile: File;
  @Input() invoiceFormSelectedRecipientEntity: number | null = null;
  @Input() invoiceFormSelectedAsset: string | null = null;
  @Input() isInvoiceFormUpdated: boolean = false;
  @Input() showGoBackDetailsButton: boolean = false;
  @Input() hidePDFViewerButton: boolean = false;
  @Input() hidePDFViewerButtonForPPR: boolean = false;
  @Input() hasMandatoryFieldChanged: boolean = true;
  @Input() primaryReferenceHasChanged: boolean = false;
  @Input() postActionCallBack: (
    actionType: InvoiceActionType,
    context?: any
  ) => void = () => {};
  @Input() canWaitInvoice: boolean = false;
  @Input() canProcessInvoice: boolean = false;

  @ViewChild(UploadComponent) uploadComponent: UploadComponent;

  constructor(
    private invoiceService: InvoiceService,
    private authenticationInfoService: AuthenticationInfoService,
    private router: Router,
    public dialog: MatDialog,
    private numberFormatPipe: NumberCommaToDotFormatPipe
  ) {}

  ngOnInit() {
    this.isInQualificationPipeline =
      !_.isNil(this.invoice) &&
      qualificationPipelineStates.includes(this.invoice.State);
    this.isInProcessedStateState =
      !_.isNil(this.invoice) && processedState.includes(this.invoice.State);
    this.isRejectedInvoice =
      this.invoiceViewType == InvoiceViewType.REJECTED &&
      !this.isInQualificationPipeline;
    this.isDispatchedInvoice =
      this.invoiceViewType == InvoiceViewType.DISPATCHED &&
      !this.isInQualificationPipeline;
    this.isSearchDetail =
      this.invoiceViewType == InvoiceViewType.SEARCH &&
      !this.isInQualificationPipeline;
    this.isControl =
      this.invoiceViewType == InvoiceViewType.CONTROL &&
      !this.isInQualificationPipeline;
    this.isPendingForWorkflowCompletionVoucher =
      this.invoiceViewType == InvoiceViewType.VOUCHERWORKFLOW &&
      !this.isInQualificationPipeline;
    this.isPendingForWorkflowCompletionPromotion =
      this.invoiceViewType == InvoiceViewType.PROMOTIONWORKFLOW &&
      !this.isInQualificationPipeline;
    this.isPendingForVoucherValidation =
      this.invoiceViewType == InvoiceViewType.VOUCHERVALIDATED &&
      !this.isInQualificationPipeline;
    this.IsInjection = this.invoiceViewType == InvoiceViewType.INJECTION;
    this.isManual = this.invoiceViewType == InvoiceViewType.MANUAL;
    this.isWaitingVoucher =
      this.invoiceViewType === InvoiceViewType.WAITING &&
      !this.isInQualificationPipeline;
    this.isNonCompliantInvoice =
      !_.isNil(this.invoice) &&
      this.invoice.RejectionFlagId === RejectionFlags.NonCompliant;
    this.isDuplicateInvoice =
      !_.isNil(this.invoice) &&
      this.invoice.RejectionFlagId === RejectionFlags.Duplicate;
    this.showValidationButton = this.isPendingForWorkflowCompletionPromotion
      ? this.userPromotionCanValidate
      : (this.isPendingForWorkflowCompletionVoucher ||
          this.isPendingForVoucherValidation) &&
        this.userCanValidate;
    this.showCancelValidationPromotionButton = this
      .isPendingForWorkflowCompletionPromotion
      ? this.canCancelValidationPromotionButton
      : false;
    this.showComplaintPromotionButton = this
      .isPendingForWorkflowCompletionPromotion
      ? this.canComplaintPromotionButton
      : true;
    this.isSupervisor =
      this.authenticationInfoService.authentificatedUser.hasGrant(
        AccessRequirement.SupervisionMembership
      );
    this.hasControlMembership =
      this.authenticationInfoService.authentificatedUser.hasGrant(
        AccessRequirement.ControlMembership
      );
    this.hasOperationalMembership =
      this.authenticationInfoService.authentificatedUser.hasGrant(
        AccessRequirement.OperationalMembership
      );
    this.isUserInCancelationPromotionGroup =
      this.authenticationInfoService.authentificatedUser.hasGrant(
        AccessRequirement.PPRCancelationMembership
      );
    this.hasAdministrationGrant =
      this.authenticationInfoService.authentificatedUser.hasGrant(
        AccessRequirement.AdministrationMembership
      );
    this.isPickedNotByMe =
      !_.isNil(this.invoice) &&
      !_.isNil(this.invoice.PickedByUserId) &&
      this.invoice.PickedByUserId !==
        this.authenticationInfoService.authentificatedUser.Id;
    this.isPickedByMe =
      !_.isNil(this.invoice) &&
      this.invoice.PickedByUserId ===
        this.authenticationInfoService.authentificatedUser.Id;
    this.isComplaintNotByMe =
      !_.isNil(this.invoice) &&
      !_.isNil(this.invoice.ComplaintByUserId) &&
      this.invoice.ComplaintByUserId !==
        this.authenticationInfoService.authentificatedUser.Id;
    this.isComplaintByMe =
      !_.isNil(this.invoice) && this.invoice.ComplaintByUserId != null;
    this.isComplaint =
      !_.isNil(this.invoice) && !_.isNil(this.invoice.ComplaintByUserId);
    this.hideInvoicePPRLogErrorHistory =
      _.isNil(this.invoice) ||
      _.isNil(this.invoice.InvoicePPRLogs) ||
      this.invoice.InvoicePPRLogsErrorCount === 0;
    this.isInErrorPPRState =
      !_.isNil(this.invoice) && this.invoice.IsInvoiceInErrorPPR;

    if (!this.isMemberAssignmentGroup) {
      this.isToControlInWaiting =
        this.isMemberControlGroup &&
        this.isWaitingVoucher &&
        !_.isNil(this.invoice) &&
        this.invoice.IsVoucher;
    }
  }

  get canValidateVoucher(): boolean {
    if (!this.isPendingForVoucherValidation) {
      return false;
    }

    if (
      !this.authenticationInfoService.authentificatedUser.GroupsMembership
        ?.NonERPComptabilityGroups?.length
    ) {
      return false;
    }

    return this.authenticationInfoService.authentificatedUser.GroupsMembership.NonERPComptabilityGroups.includes(
      this.invoice.Workflow.NonErpGroupId
    );
  }

  get isMemberControlGroup(): boolean {
    return this.authenticationInfoService.authentificatedUser.GroupsMembership.ControlGroups.includes(
      this.invoice.CurrentControlGroupIdOfAssignmentGroup
    );
  }

  get isMemberAssignmentGroup(): boolean {
    return this.authenticationInfoService.authentificatedUser.GroupsMembership.OperationalGroups.includes(
      this.invoice.CurrentAssignmentGroupId
    );
  }

  get canProcessInvoiceInternal(): boolean {
    return this.canProcessInvoice;
  }

  get canWaitInvoiceInternal(): boolean {
    return this.canWaitInvoice;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.invoiceForm?.controls;
  }

  get asRefOne(): boolean {
    return (
      !_.isNil(this.invoice.PrimaryReference) &&
      !_.isEmpty(this.invoice.PrimaryReference)
    );
  }

  get canProcess(): boolean {
    return _.isNil(this.invoiceForm) || this.invoiceForm.disabled
      ? !_.isNil(this.invoice.PrimaryReference) &&
          !_.isEmpty(this.invoice.PrimaryReference)
      : this.invoiceForm.valid;
  }

  get canWait(): boolean {
    return this.canProcess || _.isNil(this.invoice.SupplierEvaluaCompanyName);
  }

  get isEmptyEvaluaCompanyName(): boolean {
    return this.canManageInvoiceAGI
      ? false
      : _.isNil(this.invoice.SupplierEvaluaCompanyName);
  }

  get canCancelValidationPromotionButton(): boolean {
    return this.invoice.WorkflowPromotionCurrentStage?.Level !== 1;
  }

  get canComplaintPromotionButton(): boolean {
    return this.invoice.WorkflowPromotionCurrentStage?.Level !== 1;
  }

  get canUpdateInvoiceLevelOneWFPromotion(): boolean {
    return this.invoice.WorkflowPromotionCurrentStage?.Level === 1;
  }

  get canCancelProcessPromotion(): boolean {
    return (
      this.invoice.CanCancelProcessPromotion &&
      this.isUserInCancelationPromotionGroup &&
      this.invoice.State !== 'CanceledPPR'
    );
  }

  private get userCanValidate(): boolean {
    return (
      !this.invoice.WorkflowIsCompleted &&
      this.invoice.WorkflowIsActive &&
      this.authenticationInfoService.authentificatedUser.hasGroup(
        this.invoice.WorkflowCurrentStage.ValidationGroup.Id
      )
    );
  }

  private get userPromotionCanValidate(): boolean {
    return (
      !this.invoice.WorkflowIsCompleted &&
      this.invoice.WorkflowIsActive &&
      this.authenticationInfoService.authentificatedUser.hasGroup(
        this.invoice.WorkflowPromotionCurrentStage.ValidationGroup.Id
      )
    );
  }

  onSubmit(): void {
    this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) => {
        this.onUpdateInvoice()
          .pipe(
            takeUntil(this.unsubscribe$),
            filter((isConfirmed: boolean) => isConfirmed)
          )
          .subscribe((_) => {
            this.updateInvoice(primaryReferenceOnPdf);
            if (this.isWaitingVoucher) {
              this.invoiceService
                .removeFromWaitingStatus(this.invoice.Id)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe();
            }
          });
      });
  }

  goBackToDetails(): void {
    if (_.isNil(this.invoiceViewType)) {
      this.router.navigate([
        '/invoices',
        InvoiceViewType.SEARCH,
        'detail',
        this.invoice.Id,
      ]);
      return;
    }

    this.router.navigate([
      '/invoices',
      this.invoiceViewType,
      'detail',
      this.invoice.Id,
    ]);
  }

  goBackToInvoiceList(): void {
    if (_.isNil(this.invoiceViewType)) {
      this.router.navigate(['/invoices', InvoiceViewType.SEARCH]);
      return;
    }

    this.router.navigate(['/invoices', this.invoiceViewType]);
  }

  onTraceabilityDetail(): void {
    this.router.navigate(['/invoices/traceability', this.invoice.Id]);
  }

  onCancelProcess(): void {
    const dataConfig =
      new ModalHelper().getCancelInvoiceInVoucherValidatedConfig();
    const dialog = this.dialog.open(WriteCommentComponent, dataConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: { isSuccess: boolean; data: string }) => {
        if (result.isSuccess) {
          const complaint: InvoiceComplaint = new InvoiceComplaint(
            this.invoice.Id,
            result.data
          );

          this.invoiceService
            .cancelInvoiceProcessWithReason(this.invoice.Id, complaint)
            .subscribe((_) => {
              this.postActionCallBack(InvoiceActionType.CANCELPROCESS);
            });
        }
      });
  }

  onCancelProcessPromotion(): void {
    const typeFacture = this.invoice.IsWorkingSituation
      ? 'situation de travaux'
      : 'facture';
    const confimModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir annuler la validation de cette ${typeFacture} ?`
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.invoiceService
            .cancelInvoiceProcessPromotion(this.invoice.Id)
            .subscribe((_) => {
              this.postActionCallBack(InvoiceActionType.CANCELPROCESS);
            });
        }
      });
  }

  checkInvoice = (): void => {
    const addin =
      this.invoiceForm && this.hasMandatoryFieldChanged
        ? 'enregistrer et '
        : '';
    const processModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir ${addin}contrôler cette facture ?`
    );
    const dialog = this.dialog.open(ModalComponent, processModal);

    this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) =>
        dialog
          .afterClosed()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((result) => {
            if (result) {
              if (this.isWaitingVoucher) {
                this.invoiceService
                  .removeFromWaitingStatus(this.invoice.Id)
                  .pipe(takeUntil(this.unsubscribe$))
                  .subscribe((_) => {
                    this.checkAndUpdateInvoice(primaryReferenceOnPdf);
                  });
              } else {
                this.checkAndUpdateInvoice(primaryReferenceOnPdf);
              }
            }
          })
      );
  };

  checkAndUpdateInvoice = (primaryReferenceOnPdf: boolean | null): void => {
    if (this.invoiceForm && this.isInvoiceFormUpdated) {
      this.invoiceService
        .updateCheckInvoice(
          this.invoice.Id,
          this.getModel(primaryReferenceOnPdf)
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => this.goBackToInvoiceList());
      return;
    }

    this.invoiceService
      .checkInvoice(this.invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.goBackToInvoiceList());
  };

  onVoucherDetail(): void {
    if (_.isNil(this.invoiceViewType)) {
      this.router.navigate([
        '/invoices',
        InvoiceViewType.SEARCH,
        'voucher',
        this.invoice.Id,
      ]);
      return;
    }
    this.router.navigate([
      '/invoices',
      this.invoiceViewType,
      'voucher',
      this.invoice.Id,
    ]);
  }

  onForward(): void {
    const forwardModal = new ModalHelper().getForwardInvoiceModalConfig(
      this.invoice.Number,
      this.invoice.Id,
      this.invoiceViewType,
      this.isPickedNotByMe,
      this.isDispatchedInvoice || this.isControl
    );
    const dialog = this.dialog.open(InvoiceForwardComponent, forwardModal);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccess: boolean) => {
        if (isSuccess) {
          this.invoiceService
            .removeFromWaitingStatus(this.invoice.Id)
            .subscribe((_) =>
              this.postActionCallBack(InvoiceActionType.FORWARD)
            );
        }
      });
  }

  onAttach(): void {
    const attachModal = new ModalHelper().getAttachModalConfig(
      'Joindre un document',
      `Veuillez choisir un document à joindre pour la facture numéro : <strong>${this.invoice.Number}</strong>`,
      this.invoice.Id,
      this.uploadFile
    );
    const dialog = this.dialog.open(AttachmentComponent, attachModal);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccess: boolean) => {
        if (isSuccess) {
          this.postActionCallBack(InvoiceActionType.ATTACH);
        }
      });
  }

  uploadFile = (id: string, file: File): Observable<any> => {
    return this.invoiceService.attachInvoice(id, file);
  };

  onArchive(): void {
    let message = 'Êtes-vous sûr de vouloir archiver cette facture ?';
    if (this.isPickedNotByMe) {
      message +=
        '<br/><strong>Attention:</strong> Cette facture est prise en charge par un autre utilisateur.';
    }

    const modalConfig = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.invoiceService
            .archiveInvoice(this.invoice.Id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((_) => {
              this.postActionCallBack(InvoiceActionType.ARCHIVE);
            });
        }
      });
  }

  onReset(): void {
    let message = 'Êtes-vous sûr de vouloir réinitialiser cette facture ?';
    if (this.isPickedNotByMe) {
      message +=
        '<br/><strong>Attention:</strong> Cette facture est prise en charge par un autre utilisateur.';
    }

    const modalConfig = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          const invoiceDetail: InvoiceDetailModel = this.getModel(true);
          invoiceDetail.SendUpdateToSaga = false;
          this.invoiceService
            .updateInvoice(this.invoice.Id, invoiceDetail)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((_) => {
              this.invoiceService
                .resetInvoice(this.invoice.Id)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((_) => {
                  this.postActionCallBack(InvoiceActionType.RESET);
                });
            });
        }
      });
  }

  onPick(): void {
    if (this.isPickedNotByMe) {
      const confimModal = new ModalHelper().getConfirmationConfig(
        '<strong>Attention :</strong> Cette facture est déjà prise en charge par un autre utilisateur. Voulez-vous la prendre en charge ?'
      );
      const dialog = this.dialog.open(ModalComponent, confimModal);

      dialog
        .afterClosed()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((isConfirmed: boolean) => {
          if (isConfirmed) {
            this.invoiceService
              .pickInvoice(this.invoice.Id)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((_) => {
                this.isPickedByMe = true;
                this.isPickedNotByMe = false;
                this.postActionCallBack(InvoiceActionType.PICK);
              });
          }
        });

      return;
    }

    this.invoiceService
      .pickInvoice(this.invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.isPickedByMe = true;
        this.isPickedNotByMe = false;
        this.postActionCallBack(InvoiceActionType.PICK);
      });
  }

  onUnpick(): void {
    this.invoiceService
      .unpickInvoice(this.invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.isPickedByMe = false;
        this.isPickedNotByMe = false;
        this.postActionCallBack(InvoiceActionType.UNPICK);
      });
  }

  onComplaint(): void {
    const complaintConfig = new ModalHelper().getComplaintConfig();

    const dialog = this.dialog.open(WriteCommentComponent, complaintConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: { isSuccess: boolean; data: string }) => {
        if (result.isSuccess) {
          const complaint: InvoiceComplaint = new InvoiceComplaint(
            this.invoice.Id,
            result.data
          );

          this.invoiceService
            .complaintInvoice(this.invoice.Id, complaint)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((_) => {
              this.isComplaintByMe = true;
              this.isComplaintNotByMe = false;
              this.isComplaint = true;
              this.postActionCallBack(InvoiceActionType.COMPLAINT, result.data);
            });
        }
      });
  }

  onReturnOperationnal(): void {
    const returnConfig = new ModalHelper().getReturnOperationnalConfig();

    const dialog = this.dialog.open(WriteCommentComponent, returnConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: { isSuccess: boolean; data: string }) => {
        if (result.isSuccess) {
          const complaint: InvoiceComplaint = new InvoiceComplaint(
            this.invoice.Id,
            result.data
          );

          this.invoiceService
            .returnOperationnalInvoice(this.invoice.Id, complaint)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((_) => {
              this.isComplaintByMe = true;
              this.isComplaintNotByMe = false;
              this.isComplaint = false;
              this.postActionCallBack(InvoiceActionType.WAITING);
            });
        }
      });
  }

  onUncomplaint(): void {
    this.invoiceService
      .uncomplaintInvoice(this.invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.isComplaintByMe = false;
        this.isComplaintNotByMe = false;
        this.isComplaint = false;
        this.postActionCallBack(InvoiceActionType.UNCOMPLAINT);
      });
  }

  onSendBack(featureType: EmailTemplateType): void {
    const modalConfig = new ModalHelper().getCancelModalConfig(
      {
        InvoiceNumber: this.invoice.Number,
        InvoiceId: this.invoice.Id,
        SupplierEmail: this.invoice.SupplierEmail,
        FeatureType: featureType,
        ShowPickedWarning: this.isPickedNotByMe,
      },
      true
    );

    const dialog = this.dialog.open(InvoiceCancelComponent, modalConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          if (this.isWaitingVoucher || this.isDispatchedInvoice) {
            this.invoiceService
              .resetWaitingStatus(this.invoice.Id)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe();
          }
          this.postActionCallBack(InvoiceActionType.SENDBACK, { featureType });
        }
      });
  }

  onProcess = (): void => {
    if (
      this.invoice.IsPPR &&
      (this.invoice.IsForPPRDirectInvoiceBDC || this.invoice.IsWorkingSituation)
    ) {
      this.onPPRReSend();
      return;
    }

    if (this.invoice.IsVoucher) {
      this.onProcessVoucher();
      return;
    } else {
      this.onProcessInvoice();
    }
  };

  onProcessInvoice = (): void => {
    this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) => {
        if (this.invoiceForm && this.hasMandatoryFieldChanged) {
          this.invoiceService
            .updateProcessInvoice(
              this.invoice.Id,
              this.getModel(primaryReferenceOnPdf)
            )
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((_) => {
              this.postActionCallBack(InvoiceActionType.PROCESSINVOICE);
              if (this.isWaitingVoucher) {
                this.invoiceService
                  .removeFromWaitingStatus(this.invoice.Id)
                  .pipe(takeUntil(this.unsubscribe$))
                  .subscribe();
              }
            });
          this.postActionCallBack(InvoiceActionType.PROCESSINVOICE);
          return;
        }

        this.processInvoice();
        this.postActionCallBack(InvoiceActionType.PROCESSINVOICE);
      });
  };

  processInvoice = (): void => {
    this.invoiceService
      .processInvoice(this.invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        if (this.isWaitingVoucher) {
          this.invoiceService
            .removeFromWaitingStatus(this.invoice.Id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe();
        }
      });
  };

  onProcessVoucher = (): void => {
    this.processVoucher();
  };

  processVoucher = (): void => {
    if (this.isInvoiceFormUpdated) {
      this.invoiceService
        .updateInvoiceSupplier(this.invoice.Id, this.getModel(true))
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((_) => {
          this.router.navigate(['invoices/voucher/process/', this.invoice.Id]);
        });
    } else {
      this.router.navigate(['invoices/voucher/process/', this.invoice.Id]);
    }
  };

  onVoucherValidation = (): Subscription => {
    let message =
      'Êtes-vous sûr de vouloir enregistrer ce traitement comptable ?';
    if (this.isInvoiceFormUpdated) {
      message = `Etes-vous sûr de vouloir sauvegarder les informations de cette facture et d'enregistrer ce traitement comptable ?`;
    }

    const modalConfig = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    return this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) => {
        dialog
          .afterClosed()
          .pipe(
            takeUntil(this.unsubscribe$),
            filter((isConfirmed: boolean) => isConfirmed)
          )
          .subscribe((_) =>
            this.openConfirmVoucherValidation(primaryReferenceOnPdf)
          );
      });
  };

  onUpdateInvoiceWaiting() {
    this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) => {
        this.onUpdateInvoice()
          .pipe(
            takeUntil(this.unsubscribe$),
            filter((isConfirmed: boolean) => isConfirmed)
          )
          .subscribe((_) => {
            const invoiceDetail: InvoiceDetailModel = this.getModel(
              primaryReferenceOnPdf
            );
            invoiceDetail.SendUpdateToSaga = true;
            invoiceDetail.CalculatedWaitingFlag = true;

            this.invoiceService
              .updateInvoice(this.invoice.Id, invoiceDetail)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((_) => {
                this.postActionCallBack(InvoiceActionType.WAITING);
              });
          });
      });
  }

  onUpdateInvoiceWorkflow() {
    this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) => {
        this.onUpdateInvoice()
          .pipe(
            takeUntil(this.unsubscribe$),
            filter((isConfirmed: boolean) => isConfirmed)
          )
          .subscribe((_) => {
            const invoiceDetail: InvoiceDetailModel = this.getModel(
              primaryReferenceOnPdf
            );
            invoiceDetail.SendUpdateToSaga = false;

            this.invoiceService
              .updateInvoice(this.invoice.Id, invoiceDetail)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((_) => {
                this.postActionCallBack(
                  InvoiceActionType.WORKFLOWSTAGEVALIDATION
                );
              });
          });
      });
  }

  onWorkflowStageValidation = (): Subscription => {
    return this.onWorkflowStageValidationDetail();
  };

  onWorkflowStageValidationDetail = (): Subscription => {
    const isWFPromotion = this.isPendingForWorkflowCompletionPromotion;
    const wfName = isWFPromotion
      ? this.invoice.WorkflowPromotion?.Name
      : this.invoice.Workflow?.Name;
    const wfLevel = isWFPromotion
      ? this.invoice.WorkflowPromotionCurrentStage?.Level
      : this.invoice.WorkflowCurrentStage?.Level;

    const workflowStageValidationConfig =
      new ModalHelper().getWorkflowStageValidationConfig(
        this.invoice.Id,
        this.invoice.Number,
        'facture',
        wfName,
        wfLevel,
        isWFPromotion,
        this.isInvoiceFormUpdated
      );

    if (isWFPromotion) {
      workflowStageValidationConfig.data.ApproveWorkflowStage = (
        invoiceId: string,
        validation: WorkflowStageValidationModel
      ) =>
        this.invoiceService.approveCurrentWorkflowStagePromotion(
          invoiceId,
          validation
        );
    } else {
      workflowStageValidationConfig.data.ApproveWorkflowStage = (
        invoiceId: string,
        validation: WorkflowStageValidationModel
      ) =>
        this.invoiceService.approveCurrentWorkflowStage(invoiceId, validation);
    }

    const dialog = this.dialog.open(
      WorkflowStageValidationComponent,
      workflowStageValidationConfig
    );

    return dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccces: boolean) => {
        if (isSuccces) {
          this.postActionCallBack(InvoiceActionType.WORKFLOWSTAGEVALIDATION);
        }
      });
  };

  onCancel(): void {
    this.invoiceForm.reset();
    this.postActionCallBack(InvoiceActionType.CANCELFORM);
  }

  onUpdateInvoice(): Observable<any> {
    const message = this.getUpdateMessage();

    const confimModal = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, confimModal);

    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  getUpdateMessage(): string {
    if (this.isRejectedInvoice) {
      let message =
        'Êtes-vous sûr de vouloir modifier cette facture ?<br/><i>Ces modifications sont susceptibles de changer le statut ou le groupe de cette facture.</i>';
      if (this.isPickedNotByMe) {
        message +=
          '<br/><strong>Attention :</strong> Cette facture est prise en charge par un autre utilisateur.';
      }
      return message;
    }

    if (this.isPendingForVoucherValidation) {
      return 'Êtes-vous sûr de vouloir modifier les informations de cette facture ?';
    }

    return 'Êtes-vous sûr de vouloir modifier ces informations ?<br/><strong>Attention :</strong> Ces modifications sont susceptibles de changer le statut ou le groupe de cette facture.';
  }

  onPrimaryReferenceUpdated(): Observable<boolean | null> {
    if (!this.primaryReferenceHasChanged) {
      return of(null);
    }

    const message = `<strong>Aidez-nous à réduire les erreurs sur la REF1</strong>.<br/>La référence 1 est-elle <u><strong>présente et correcte</strong></u> sur la facture PDF ?`;
    const modalConfig = new ModalHelper().getConfirmationConfig(
      message,
      'Oui',
      'Non'
    );
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  openConfirmVoucherValidation = (
    primaryReferenceOnPdf: boolean | null
  ): void => {
    let serviceCall$ = this.invoiceService.validateVoucher(this.invoice.Id);
    if (this.isInvoiceFormUpdated) {
      serviceCall$ = this.invoiceService.updateValidateVoucher(
        this.invoice.Id,
        this.getModel(primaryReferenceOnPdf)
      );
    }

    serviceCall$.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
      if (this.isWaitingVoucher) {
        this.invoiceService
          .removeFromWaitingStatus(this.invoice.Id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe();
      }
      this.postActionCallBack(InvoiceActionType.VOUCHERVALIDATION);
    });
  };

  updateInvoice(primaryReferenceOnPdf?: boolean | null): void {
    const invoiceDetail: InvoiceDetailModel = this.getModel(
      primaryReferenceOnPdf
    );

    this.invoiceService
      .updateInvoice(this.invoice.Id, invoiceDetail)
      .subscribe(() => {
        this.postActionCallBack(InvoiceActionType.UPDATEINVOICE);
      });
  }

  getModel(primaryReferenceOnPdf?: boolean | null): InvoiceDetailModel {
    const invoiceDetail: InvoiceDetailModel = Object.assign({}, this.invoice);
    invoiceDetail.Number = this.formControls.Number.value;
    invoiceDetail.SupplierSiren = this.formControls.SupplierSiren.value;
    invoiceDetail.SupplierSiret = this.formControls.SupplierSiret.value;
    invoiceDetail.SupplierCompanyName =
      this.formControls.SupplierCompanyName.value;
    invoiceDetail.SupplierRcsNumber = this.formControls.SupplierRcsNumber.value;
    invoiceDetail.SupplierVatNumber = this.formControls.SupplierVatNumber.value;
    invoiceDetail.RecipientEntityId = this.invoiceFormSelectedRecipientEntity;
    invoiceDetail.PrimaryReference = this.invoiceFormSelectedAsset;
    invoiceDetail.SecondaryReference =
      this.formControls.SecondaryReference.value;
    invoiceDetail.IssuedAt = this.formControls.IssuedAt.value;
    invoiceDetail.DueDate =
      moment(this.formControls.DueDate.value).isValid &&
      this.formControls.DueDate.value != 'Invalid date'
        ? this.formControls.DueDate.value
        : null;
    invoiceDetail.EstimatedActDate =
      moment(this.formControls.EstimatedActDate.value).isValid &&
      this.formControls.EstimatedActDate.value != 'Invalid date'
        ? this.formControls.EstimatedActDate.value
        : null;
    invoiceDetail.AmountTaxExcluded = this.numberFormatPipe.transform(
      this.formControls.AmountTaxExcluded.value
    );
    invoiceDetail.AmountTaxIncluded = this.numberFormatPipe.transform(
      this.formControls.AmountTaxIncluded.value
    );
    invoiceDetail.Currency = this.formControls.Currency.value;
    invoiceDetail.IsReverseFactoring =
      this.formControls.IsReverseFactoring.value;
    invoiceDetail.VoucherAnalyticalCode =
      this.formControls.VoucherAnalyticalCode.value;
    invoiceDetail.VoucherAnalyticalSection =
      this.formControls.VoucherAnalyticalSection.value;
    invoiceDetail.VoucherComment = this.formControls.VoucherComment.value;
    invoiceDetail.VoucherDepartment = this.formControls.VoucherDepartment.value;
    invoiceDetail.PrimaryReferenceOnPdf = primaryReferenceOnPdf;
    invoiceDetail.SupplierEvaluaCompanyName = this.formControls
      .SupplierEvaluaCompanyName.value
      ? this.formControls.SupplierEvaluaCompanyName.value[0]
      : null;
    invoiceDetail.SupplierMdmId = this.invoice.SupplierIdFournisseur;
    invoiceDetail.ClientIdPPR = this.formControls.ClientIdPPR.value;
    invoiceDetail.GroupIdPPR = this.formControls.GroupIdPPR.value;

    invoiceDetail.ThirdPartyPayerId = this.formControls.ThirdPartyPayerId.value
      ? this.formControls.ThirdPartyPayerId.value
      : null;
    invoiceDetail.ThirdPartyPayerName = this.formControls.ThirdPartyPayerName
      .value
      ? this.formControls.ThirdPartyPayerName.value[0]
      : null;

    invoiceDetail.IsForPPRDirectInvoiceBDC = this.formControls
      .IsForPPRDirectInvoiceBDC.value
      ? this.formControls.IsForPPRDirectInvoiceBDC.value
      : false;
    invoiceDetail.IsWorkingSituation = this.formControls.IsWorkingSituation
      .value
      ? this.formControls.IsWorkingSituation.value
      : false;
    invoiceDetail.OperationId = this.formControls.OperationId.value
      ? this.formControls.OperationId.value[0]
      : null;
    invoiceDetail.OperationName = this.formControls.OperationName.value
      ? this.formControls.OperationName.value
      : null;
    invoiceDetail.CommandeId = this.formControls.CommandeId.value
      ? this.formControls.CommandeId.value
      : null;
    invoiceDetail.CommandeInternalNumber = this.formControls
      .CommandeInternalNumber.value
      ? this.formControls.CommandeInternalNumber.value
      : null;
    invoiceDetail.OperationSocieteId =
      this.formControls.OperationSocieteId.value;
    invoiceDetail.OperationAssistantName = this.invoice.OperationAssistantName;
    invoiceDetail.InvoicePPRType = this.formControls.InvoicePPRType.value
      ? this.formControls.InvoicePPRType.value[0]
      : null;

    if (
      invoiceDetail.IsPPR &&
      invoiceDetail.IsWorkingSituation &&
      !this.isRejectedInvoice
    ) {
      invoiceDetail.AmountTaxExcludedWithoutAmendment =
        this.numberFormatPipe.transform(
          this.formControls.AmountTaxExcludedWithoutAmendment.value
        );
      invoiceDetail.VatCode = this.formControls.VatCode.value
        ? this.formControls.VatCode.value[0].Code
        : null;
      invoiceDetail.SituationReference =
        this.formControls.SituationReference.value;
      invoiceDetail.SituationPeriode = this.formControls.SituationPeriode.value;
      invoiceDetail.MarketId = this.formControls.MarketId.value;
      invoiceDetail.TradeId = this.formControls.TradeId.value;
      invoiceDetail.TradeLabel =
        this.formControls.TradeLabel.value.length > 0
          ? this.formControls.TradeLabel.value[0].libelleCorpsMetier
          : null;
      invoiceDetail.AmountTradeExcludingTax = this.numberFormatPipe.transform(
        this.formControls.AmountTradeExcludingTax.value
      );
      invoiceDetail.CumulationPreviousSituationAmountTaxExcluded =
        this.numberFormatPipe.transform(
          this.formControls.CumulationPreviousSituationAmountTaxExcluded.value
        );
    }

    if (invoiceDetail.IsPPR && invoiceDetail.IsForPPRDirectInvoiceBDC) {
      invoiceDetail.VatCode = null;
      invoiceDetail.SituationReference = null;
      invoiceDetail.SituationPeriode = null;
      invoiceDetail.MarketId = null;
      invoiceDetail.TradeId = null;
      invoiceDetail.TradeLabel = null;
      invoiceDetail.AmountTradeExcludingTax = null;
    }

    invoiceDetail.Expenses = [];
    invoiceDetail.Amendments = [];
    invoiceDetail.Deductions = [];
    invoiceDetail.Delegations = [];

    if (
      invoiceDetail.IsPPR &&
      invoiceDetail.IsForPPRDirectInvoiceBDC &&
      !this.isRejectedInvoice
    ) {
      const formExpenses = this.formControls
        .InvoiceExpenses as UntypedFormArray;
      formExpenses.controls.forEach((element) => {
        let formGroup = element as UntypedFormGroup;
        let expense: InvoiceExpenseDetail = {
          Id: formGroup.controls.ExpenseId.value,
          InvoiceId: this.invoice.Id,
          CreatedAt: formGroup.controls.CreatedAt.value,
          FamilyCode: formGroup.controls.FamilyCode.value
            ? formGroup.controls.FamilyCode.value[0]
            : null,
          FamilyLabel: formGroup.controls.FamilyLabel.value
            ? formGroup.controls.FamilyLabel.value[0]
            : null,
          FamilyAccount: formGroup.controls.FamilyAccount.value
            ? formGroup.controls.FamilyAccount.value[0]
            : null,
          Label: formGroup.controls.ExpenseLabel.value,
          VatCode:
            this.isWaitingVoucher && !this.isInErrorPPRState
              ? formGroup.controls.ExpenseVat.value
              : formGroup.controls.ExpenseSelectedVat.value[0].Code,
          AmountTaxExcluded: this.numberFormatPipe.transform(
            formGroup.controls.ExpenseAmountTaxExcluded.value
          ),
          AmountVat: this.numberFormatPipe.transform(
            formGroup.controls.ExpenseAmountVat.value
          ),
          AmountTaxIncluded: this.numberFormatPipe.transform(
            formGroup.controls.ExpenseAmountTaxIncluded.value
          ),
        };
        invoiceDetail.Expenses.push(expense);
      });
    }

    if (
      invoiceDetail.IsPPR &&
      invoiceDetail.IsWorkingSituation &&
      !this.isRejectedInvoice
    ) {
      // Amendments
      const formAmendments = this.formControls
        .InvoiceAmendments as UntypedFormArray;
      if (formAmendments) {
        formAmendments.controls.forEach((element) => {
          let formGroup = element as UntypedFormGroup;

          let amendment: InvoiceAmendmentDetail = {
            Id: formGroup.controls.AmendmentId.value,
            InvoiceId: this.invoice.Id,
            CreatedAt: formGroup.controls.CreatedAt.value,
            Code: formGroup.controls.AmendmentCode.value
              ? formGroup.controls.AmendmentCode.value
              : null,
            Label:
              formGroup.controls.AmendmentLabel.value.length > 0
                ? formGroup.controls.AmendmentLabel.value[0].label
                : null,
            AmountTaxExcluded: this.numberFormatPipe.transform(
              formGroup.controls.AmendmentAmountTaxExcluded.value
            ),
            AmountVat: this.numberFormatPipe.transform(
              formGroup.controls.AmendmentAmountVat.value
            ),
            AmountTaxIncluded: this.numberFormatPipe.transform(
              formGroup.controls.AmendmentAmountTaxIncluded.value
            ),
          };
          invoiceDetail.Amendments.push(amendment);
        });
      }

      // Deductions
      const formDeductions = this.formControls
        .InvoiceDeductions as UntypedFormArray;
      if (formDeductions) {
        formDeductions.controls.forEach((element) => {
          let formGroup = element as UntypedFormGroup;
          let deduction: InvoiceDeductionDetail = {
            Id: formGroup.controls.DeductionId.value,
            InvoiceId: this.invoice.Id,
            CreatedAt: formGroup.controls.CreatedAt.value,
            Code: formGroup.controls.DeductionTypeCode.value,
            Label: formGroup.controls.DeductionTypeLabel.value[0].Label,
            AmountProvisionedTaxExcluded: this.numberFormatPipe.transform(
              formGroup.controls.AmountProvisionedTaxExcluded.value
            ),
            AmountReleasedTaxExcluded: this.numberFormatPipe.transform(
              formGroup.controls.AmountReleasedTaxExcluded.value
            ),
            AmountRetainedTaxExcluded: this.numberFormatPipe.transform(
              formGroup.controls.AmountRetainedTaxExcluded.value
            ),
          };
          invoiceDetail.Deductions.push(deduction);
        });
      }

      // Delegations
      const formDelegations = this.formControls
        .InvoiceDelegations as UntypedFormArray;
      if (formDelegations) {
        formDelegations.controls.forEach((element) => {
          let formGroup = element as UntypedFormGroup;
          let delegation: InvoiceDelegationDetail = {
            Id: formGroup.controls.DelegateInternalId.value,
            InvoiceId: this.invoice.Id,
            CreatedAt: formGroup.controls.CreatedAt.value,
            CreditorId: formGroup.controls.CreditorId.value,
            CreditorName: formGroup.controls.CreditorName.value[0],
            DelegateId: formGroup.controls.DelegateId.value,
            DelegateName: formGroup.controls.DelegateName.value[0],
            AmountRemainsTaxIncluded: this.numberFormatPipe.transform(
              formGroup.controls.DelegateAmountRemainsTaxIncluded.value
            ),
            AmountPackageTaxIncluded: this.numberFormatPipe.transform(
              formGroup.controls.DelegateAmountPackageTaxIncluded.value
            ),
          };
          invoiceDetail.Delegations.push(delegation);
        });
      }
    }

    if (this.isManual) {
      invoiceDetail.FileForCreate = this.invoice.FileForCreate;
    }

    invoiceDetail.SendUpdateToSaga = true;

    return invoiceDetail;
  }

  onInject() {
    const message =
      'Voulez-vous réinjecter cette facture dans le circuit classique de traitement ?';

    const confimModal = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, confimModal);

    return dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.injectInvoice();
        }
      });
  }

  injectInvoice() {
    const model = this.getInjectionModel();

    this.invoiceService.injectInvoice(model).subscribe(() => {
      this.postActionCallBack(InvoiceActionType.INJECTINVOICE);
    });
  }

  getInjectionModel(): InvoiceInjectionModel {
    const invoiceInjection: InvoiceInjectionModel = new InvoiceInjectionModel();
    invoiceInjection.Number = this.formControls.Number.value;
    invoiceInjection.SupplierSiren = this.formControls.SupplierSiren.value;
    invoiceInjection.SupplierSiret = this.formControls.SupplierSiret.value;
    invoiceInjection.SupplierCompanyName =
      this.formControls.SupplierCompanyName.value;
    invoiceInjection.SupplierRcsNumber =
      this.formControls.SupplierRcsNumber.value;
    invoiceInjection.SupplierVatNumber =
      this.formControls.SupplierVatNumber.value;
    invoiceInjection.RecipientEntityId =
      this.invoiceFormSelectedRecipientEntity;
    invoiceInjection.PrimaryReference = this.invoiceFormSelectedAsset;
    invoiceInjection.SecondaryReference =
      this.formControls.SecondaryReference.value;
    invoiceInjection.IssuedAt = this.formControls.IssuedAt.value;
    invoiceInjection.DueDate =
      moment(this.formControls.DueDate.value).isValid &&
      this.formControls.DueDate.value != 'Invalid date'
        ? this.formControls.DueDate.value
        : null;
    invoiceInjection.AmountTaxExcluded = this.numberFormatPipe.transform(
      this.formControls.AmountTaxExcluded.value
    );
    invoiceInjection.AmountTaxIncluded = this.numberFormatPipe.transform(
      this.formControls.AmountTaxIncluded.value
    );
    invoiceInjection.Currency = this.formControls.Currency.value;
    invoiceInjection.IsReverseFactoring =
      this.formControls.IsReverseFactoring.value;
    invoiceInjection.ImportDate = this.injection?.ImportDate;
    invoiceInjection.ImportMissingInvoiceNumberDocumentId =
      this.injection?.ImportMissingInvoiceNumberDocumentId;
    invoiceInjection.InvoiceDocuwareDocumentFileName =
      this.injection?.InvoiceDocuwareDocumentFileName;
    invoiceInjection.InvoiceDocuwareDocumentId =
      this.injection?.InvoiceDocuwareDocumentId;
    invoiceInjection.OcrDate = this.injection?.OcrDate;
    invoiceInjection.Source = this.injection?.Source;
    invoiceInjection.SupplierMdmId = this.invoice.SupplierIdFournisseur;
    invoiceInjection.SupplierEvaluaCompanyName = this.formControls
      .SupplierEvaluaCompanyName.value
      ? this.formControls.SupplierEvaluaCompanyName.value[0]
      : null;
    invoiceInjection.SupplierSageErpCode = this.invoice.SupplierSageErpCode;
    return invoiceInjection;
  }

  onManualCreate() {
    const message =
      "Voulez-vous créer cette facture et l'insérer dans le circuit classique de traitement ?";

    const confimModal = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, confimModal);

    return dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.manualInvoice();
        }
      });
  }

  manualInvoice() {
    const model = this.getModel(true);
    this.invoiceService.manualInvoice(model).subscribe(() => {
      this.postActionCallBack(InvoiceActionType.MANUAL);
    });
  }

  openWaitingStatusDialog(): void {
    const addin = this.isInvoiceFormUpdated ? 'enregistrer et ' : '';
    const processModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir ${addin}envoyer vers "Factures en attente" ?`
    );

    const dialog = this.dialog.open(ModalComponent, processModal);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.sendToWaitingStatus();
        }
      });
  }

  sendToWaitingStatus(): void {
    this.onPrimaryReferenceUpdated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((primaryReferenceOnPdf: boolean | null) => {
        if (this.invoiceForm) {
          let invoiceDetail: InvoiceDetailModel = this.getModel(
            primaryReferenceOnPdf
          );

          invoiceDetail.SendUpdateToSaga = false;
          invoiceDetail.CalculatedWaitingFlag = true;

          this.invoiceService
            .updateInvoice(this.invoice.Id, invoiceDetail)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
              this.postActionCallBack(InvoiceActionType.UPDATEINVOICE);
            });
        } else {
          this.invoiceService
            .sendToWaitingStatus(this.invoice.Id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
              this.postActionCallBack(InvoiceActionType.UPDATEINVOICE);
            });
        }
      });
  }

  findInvalidControls(): void {
    console.log(this.invoiceForm);

    const controls = this.invoiceForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        console.log(name);
      }
    }
  }

  onInvoicePPRLogErrorHistory(): void {
    this.invoiceService
      .getErrorsPPRById(this.invoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((invoicePPRLogs: InvoicePPRLogModel[]) => {
        this.invoice.InvoicePPRLogs = invoicePPRLogs;
        const modalConfig = new ModalHelper().getInvoicePPRLogsConfig(
          this.invoice
        );
        this.dialog.open(InvoiceListPPRErrorsComponent, modalConfig);
      });
  }

  onPPRReSend(): void {
    const addin = this.isInvoiceFormUpdated ? 'enregistrer et ' : '';
    const typeInvoice = this.invoice.IsForPPRDirectInvoiceBDC
      ? 'facture'
      : 'situation de travaux';
    const typeSend = this.isDispatchedInvoice ? 'envoyer' : 'renvoyer';
    const confimModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir ${addin}${typeSend} la ${typeInvoice} à PPR ?`,
      'Oui',
      'Non'
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          if (this.isInvoiceFormUpdated) {
            this.onPrimaryReferenceUpdated()
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((primaryReferenceOnPdf: boolean | null) => {
                const invoiceDetail: InvoiceDetailModel = this.getModel(
                  primaryReferenceOnPdf
                );
                invoiceDetail.SendUpdateToSaga = false;
                this.invoiceService
                  .updateInvoice(this.invoice.Id, invoiceDetail)
                  .subscribe(() => {
                    this.onPPRReSendDetail();
                  });
              });
          } else {
            this.onPPRReSendDetail();
          }
        }
      });
  }

  onPPRReSendDetail(): void {
    if (this.isDispatchedInvoice) {
      this.invoiceService.sendInvoicePPR(this.invoice.Id).subscribe((_) => {
        if (this.isWaitingVoucher) {
          this.postActionCallBack(InvoiceActionType.MANUAL);
        }
      });
      if (!this.isWaitingVoucher) {
        this.postActionCallBack(InvoiceActionType.MANUAL);
      }
    } else {
      this.invoiceService.reSendInvoicePPR(this.invoice.Id).subscribe((_) => {
        if (this.isWaitingVoucher) {
          this.postActionCallBack(InvoiceActionType.WAITING);
        }
      });
      if (!this.isWaitingVoucher) {
        this.postActionCallBack(InvoiceActionType.WAITING);
      }
    }
  }

  onFilesChange(selectedFiles: FileList): void {
    if (_.isNil(selectedFiles) || selectedFiles.length === 0) {
      return;
    }

    if (_.isNil(selectedFiles) || selectedFiles.length > 1) {
      const modalConfig = new ModalHelper().getGenericMessageModalConfig(
        `Vous ne pouvez pas joindre plus d'un fichier à cette facture lors de la création manuelle.`
      );
      const dialog = this.dialog.open(ModalComponent, modalConfig);
      dialog
        .afterClosed()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          return;
        });
    }

    this.file = selectedFiles[0];
    this.getInvoiceFile();
  }

  getInvoiceFile() {
    let invoiceFile: InvoiceFilesForCreate;
    const reader = new FileReader();
    reader.readAsDataURL(this.file);

    reader.onload = () => {
      invoiceFile = new InvoiceFilesForCreate(
        this.file.name,
        reader.result.toString()
      );
      this.filename = this.file.name;
      this.invoice.FileForCreate = invoiceFile;
    };
  }

  get isInvalideCtrlAmount(): boolean {
    return !this.invoice.CanProcessInvoice;
  }

  get isDispatchedProcessDisabled(): boolean {
    const process =
      this.canProcess &&
      this.canProcessInvoice &&
      !this.canWaitInvoice &&
      !this.hasMandatoryFieldChanged;
    return !process;
  }

  get isDispatchedModifyDisabled(): boolean {
    const modified =
      (this.isInvoiceFormUpdated && this.invoiceForm.valid) ||
      (this.isInvoiceFormUpdated &&
        !this.hasMandatoryFieldChanged &&
        this.isInvalidEvalua);
    return !modified;
  }

  get isDispatchedWaitingDisabled(): boolean {
    const isWaiting =
      (this.isInvalidEvalua && !this.invoiceForm.valid) ||
      (this.invoiceForm.valid && this.canWaitInvoice);
    return !isWaiting;
  }

  get isControlWaitingDisabled(): boolean {
    const isWaiting =
      (this.isInvalidEvalua && !this.invoiceForm.valid) ||
      (this.invoiceForm.valid && this.canWaitInvoice);
    return !isWaiting;
  }

  get isSaveWaitingDisabled(): boolean {
    if (
      this.isInvalidEvalua ||
      this.countInvalidFamilyCode === this.countInvalidControls
    )
      return false;
    return !this.hasMandatoryFieldChanged;
  }

  get isSaveManualDisabled(): boolean {
    if (!this.isPDFOnManualSet) return true;
    if (this.countInvalidFamilyCode === this.countInvalidControls) return false;
    return !this.invoiceForm.valid || !this.isPDFOnManualSet;
  }

  get isInvalidEvalua(): boolean {
    return (
      this.countInvalidControls === 1 &&
      _.isNil(this.invoice.SupplierEvaluaCompanyName) &&
      _.isNil(this.invoice.ClientIdPPR) &&
      _.isNil(this.invoice.GroupIdPPR)
    );
  }

  get isPDFOnManualSet(): boolean {
    return !_.isNil(this.invoice.FileForCreate);
  }

  get countInvalidFamilyCode(): number {
    return !this.invoice.Expenses
      ? 0
      : this.invoice.Expenses.filter((o) => _.isNil(o.FamilyCode)).length;
  }

  get countInvalidControls(): number {
    let countInvalid = 0;
    if (!this.invoiceForm) {
      return countInvalid;
    } else if (this.invoiceForm.valid) {
      return countInvalid;
    } else {
      const controls = this.invoiceForm.controls;
      for (const name in controls) {
        const ctrl = controls[name];
        if (ctrl instanceof UntypedFormControl) {
          if (ctrl.invalid) {
            countInvalid = countInvalid + 1;
          }
        } else if (ctrl instanceof UntypedFormArray) {
          ctrl.controls.forEach((element, index) => {
            const ctrlArray = (element as UntypedFormGroup).controls;
            for (const nameInArray in ctrlArray) {
              if (ctrlArray[nameInArray].invalid) {
                countInvalid = countInvalid + 1;
              }
            }
          });
        }
      }
    }
    return countInvalid;
  }
}
